import { useState } from 'react';
import { Search } from 'lucide-react';
import { useEstates, useFilterOptions } from '../hooks/useEstates';
import { EstateCard, FilterPanel, LoadingSpinner } from '../components';
import type { EstateFilters } from '../types';

export function BrowsePage() {
  const [filters, setFilters] = useState<EstateFilters>({});
  const [searchQuery, setSearchQuery] = useState('');

  const { data: estates, isLoading, error } = useEstates({
    ...filters,
    search: searchQuery || undefined,
  });
  const { data: filterOptions, isLoading: isLoadingFilters } = useFilterOptions();

  return (
    <div className="pb-8">
      {/* Hero / intro - serif heading, slate body, mirrors web-2 intro section */}
      <section className="relative w-full overflow-hidden py-12 mb-2">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] to-transparent" />
        <div className="relative max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-foreground leading-tight">
            Our Castle Network
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">
            Discover our curated collection of historic properties across the globe
          </p>
        </div>
      </section>

      <div className="px-4 space-y-5 max-w-7xl mx-auto">
        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search estates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 rounded-sm border border-white/10 bg-white/5 pl-10 pr-4 text-sm text-foreground outline-none transition focus:border-white/30 focus:bg-white/10"
          />
        </div>

        {/* Filters */}
        <FilterPanel
          filters={filters}
          options={filterOptions}
          onChange={setFilters}
          isLoading={isLoadingFilters}
        />

        {/* Results count */}
        {estates && (
          <p className="text-xs uppercase tracking-[0.24em] text-slate-400">
            {estates.length} {estates.length === 1 ? 'estate' : 'estates'} found
          </p>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="border border-destructive/40 bg-destructive/10 rounded-sm p-4 text-center">
            <p className="text-destructive">
              Unable to load estates. Please try again.
            </p>
          </div>
        )}

        {/* Estates grid */}
        {estates && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children pt-2">
            {estates.map((estate) => (
              <EstateCard key={estate.id} estate={estate} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {estates && estates.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-7 h-7 text-slate-400" />
            </div>
            <p className="font-serif text-xl text-foreground mb-2">No estates found</p>
            <p className="text-sm text-slate-400">
              Try adjusting your filters or search query
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
